import { useEffect, useRef, useState } from "react";

function useInView(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, inView };
}

const tiers = [
  {
    name: "覚醒者",
    nameEn: "AWAKENED",
    phrase: "最初の光が灯る",
    price: "¥10,000",
    desc: "王国の門が、静かに開く。\n石の声を初めて聴く者。\n儀式への招待状が届く。",
    accent: "rgba(201,168,76,0.3)",
  },
  {
    name: "誓約者",
    nameEn: "COVENANT",
    phrase: "魂に刻まれる",
    price: "¥30,000",
    desc: "忠誠は深まり、波動は高まる。\n秘儀の書へのアクセスが開かれ、\n魂の記録が王国に刻まれる。",
    accent: "rgba(201,168,76,0.6)",
    featured: true,
  },
  {
    name: "永命者",
    nameEn: "IMMORTAL",
    phrase: "時を超えた存在へ",
    price: "¥100,000",
    desc: "これは称号ではなく、存在の証明。\n王国の最奥に座し、\n宇宙の意志と一体となる。",
    accent: "rgba(201,168,76,0.9)",
  },
];

export default function RitualMembership() {
  const { ref, inView } = useInView(0.1);

  return (
    <section className="relative bg-[#0a0809] py-20 md:py-40 px-4 md:px-6 overflow-hidden">
      {/* Background image */}
      <div
        className="absolute inset-0 bg-cover bg-center opacity-8"
        style={{
          backgroundImage:
            "url(https://public.readdy.ai/ai/img_res/4120aeeca0d4913480b8a0828ed78a35.jpg)",
          filter: "brightness(0.15) saturate(0.3)",
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0a0809]/80 via-transparent to-[#0a0809]/80" />

      <div className="absolute left-1/2 top-0 -translate-x-1/2 w-px h-24 bg-gradient-to-b from-transparent to-[#c9a84c]/20" />

      <div ref={ref} className="max-w-5xl mx-auto relative z-10">
        <div
          className="text-center mb-12 md:mb-24"
          style={{
            opacity: inView ? 1 : 0,
            transition: "opacity 1.5s ease",
          }}
        >
          <div className="text-[#c9a84c]/40 text-xs tracking-[0.4em] md:tracking-[0.5em] uppercase mb-4 md:mb-6">Membership</div>
          <h2
            className="text-2xl md:text-4xl font-light text-white/80 mb-4"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            選ばれた者のための導線
          </h2>
          <p className="text-white/20 text-xs md:text-sm tracking-[0.2em]">
            忠誠を誓う者のみが与えられる称号
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/5">
          {tiers.map((tier, i) => (
            <div
              key={i}
              className="relative bg-[#0a0809] p-6 md:p-12 group cursor-pointer"
              style={{
                opacity: inView ? 1 : 0,
                transform: inView ? "translateY(0)" : "translateY(24px)",
                transition: `opacity 1.5s ease ${i * 0.2}s, transform 1.5s ease ${i * 0.2}s`,
              }}
            >
              {/* Top accent line */}
              {tier.featured && (
                <div
                  className="absolute top-0 left-0 right-0 h-px"
                  style={{ background: `linear-gradient(to right, transparent, ${tier.accent}, transparent)` }}
                />
              )}

              {/* Tier label */}
              <div className="text-[#c9a84c]/30 text-xs tracking-[0.3em] md:tracking-[0.4em] uppercase mb-4 md:mb-6 group-hover:text-[#c9a84c]/50 transition-colors duration-700">
                {tier.nameEn}
              </div>

              {/* Name */}
              <div
                className="text-white/70 text-xl md:text-2xl font-light mb-2 group-hover:text-white/90 transition-colors duration-700"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                {tier.name}
              </div>

              {/* Phrase */}
              <div
                className="text-[#c9a84c]/50 text-sm tracking-[0.15em] md:tracking-[0.2em] mb-6 md:mb-8 group-hover:text-[#c9a84c]/70 transition-colors duration-700"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                {tier.phrase}
              </div>

              {/* Divider */}
              <div
                className="w-8 h-px mb-6 md:mb-8 transition-all duration-700 group-hover:w-16"
                style={{ background: tier.accent }}
              />

              {/* Description */}
              <p
                className="text-white/30 text-sm leading-loose mb-8 md:mb-10 whitespace-pre-line group-hover:text-white/50 transition-colors duration-700"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                {tier.desc}
              </p>

              {/* Price */}
              <div className="mt-auto">
                <div className="text-white/20 text-xs tracking-[0.3em] mb-1">月額</div>
                <div
                  className="text-2xl font-light transition-colors duration-700"
                  style={{
                    fontFamily: "'Cormorant Garamond', serif",
                    color: tier.featured ? "rgba(201,168,76,0.8)" : "rgba(255,255,255,0.5)",
                  }}
                >
                  {tier.price}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div
          className="text-center mt-10 md:mt-16"
          style={{
            opacity: inView ? 1 : 0,
            transition: "opacity 2s ease 1s",
          }}
        >
          <p className="text-white/15 text-xs tracking-[0.2em] md:tracking-[0.3em]">
            Wave Level System により、継続するほど深みへ
          </p>
        </div>
      </div>
    </section>
  );
}
